import { IPaginatedResponse, IPaginateParams } from "#src/@types/api";
import endpoint from "#src/config/endpoint";
import request from "#src/utils/request";
import { TFunction } from "i18next";
import { FAKE_DIRECTORY } from "./fakeData";
import {
  IDirectoryDepartment,
  IDirectoryUser,
  IDirectoryDepartmentWithPath,
} from "./types";

// TODO: remove later
const IS_FAKE = !!process.env.REACT_APP_IS_FAKE;
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// --------------------- USERS ---------------------

async function listUsers(
  workspaceId: string,
  params: IPaginateParams & {
    sort: string | undefined;
    vpn_enabled?: string | undefined;
    department_id: string | undefined;
  }
): Promise<IPaginatedResponse<IDirectoryUser>> {
  if (IS_FAKE) {
    console.log("calling listDLPDrives", workspaceId, params);
    const res: IPaginatedResponse<IDirectoryUser> = {
      count: FAKE_DIRECTORY.users.length,
      results: FAKE_DIRECTORY.users,
    };
    await delay(1000);
    return res;
  }

  const res = await request.get<never, IPaginatedResponse<IDirectoryUser>>(
    `${endpoint.WORKSPACES}/${workspaceId}/directory/users`,
    { params }
  );
  return res;
}

async function multipleUpdateVPN(
  workspaceId: string,
  userIds: string[],
  enabled: boolean
): Promise<void> {
  if (IS_FAKE) {
    console.log("calling multipleUpdateVPN", workspaceId, userIds, enabled);
    await delay(1000);
    return;
  }

  await request.post<never, void>(
    `${endpoint.WORKSPACES}/${workspaceId}/directory/users/vpn`,
    { ids: userIds, vpn_enabled: enabled }
  );
  return;
}

async function editUser(
  workspaceId: string,
  userId: string,
  data: {
    job_title?: string;
    department_id?: string | null;
    vpn_enabled?: boolean;
  }
): Promise<void> {
  if (IS_FAKE) {
    console.log("calling editUser", workspaceId, userId, data);
    await delay(1000);
    return;
  }

  await request.put<never, void>(
    `${endpoint.WORKSPACES}/${workspaceId}/directory/users/${userId}`,
    data
  );
  return;
}

async function syncUsersWithGoogle(
  workspaceId: string,
  params: {
    code: string;
    redirect_uri: string;
  }
): Promise<void> {
  if (IS_FAKE) {
    console.log("calling syncUsersWithGoogle", workspaceId, params);
    await delay(1000);
    return;
  }

  await request.post<never, void>(
    `${endpoint.WORKSPACES}/${workspaceId}/directory/users/sync/google`,
    params
  );
  return;
}

async function exportUsers(
  workspaceId: string,
  t: TFunction<"endpoint", "directory">
): Promise<void> {
  const res = await request.get<never, IDirectoryUser[]>(
    `${endpoint.WORKSPACES}/${workspaceId}/directory/users`,
    { params: { paging: 0 } }
  );

  // Convert users data to CSV format
  const csvHeaders = [
    "Email",
    t("table.columns.name"),
    t("table.columns.department"),
    t("table.columns.title"),
    t("table.columns.devices"),
    t("table.columns.added_at"),
  ];

  const csvRows = res.map((user) => [
    user.email,
    user.full_name,
    user.department?.name || "",
    user.job_title,
    user.devices.map((device) => device.name).join("; "),
    new Date(user.created_time * 1000).toISOString(),
  ]);

  // Create CSV content
  const csvContent = [
    csvHeaders.join(","),
    ...csvRows.map((row) =>
      row
        .map((field) =>
          // Escape fields that contain commas, quotes, or newlines
          typeof field === "string" &&
          (field.includes(",") || field.includes('"') || field.includes("\n"))
            ? `"${field.replace(/"/g, '""')}"`
            : field
        )
        .join(",")
    ),
  ].join("\n");

  // Create and trigger download
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.setAttribute("href", url);
  link.setAttribute(
    "download",
    `users_export_${new Date().toISOString().split("T")[0]}.csv`
  );
  link.style.visibility = "hidden";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up the URL object
  URL.revokeObjectURL(url);
}

// --------------------- DEPARTMENTS ---------------------

async function listAllDepartments(
  workspaceId: string,
  params?: {
    q: string | undefined;
  }
): Promise<IDirectoryDepartment[]> {
  if (IS_FAKE) {
    console.log("calling listAllDepartments", workspaceId);
    const res = FAKE_DIRECTORY.departments;
    await delay(1000);
    return res;
  }

  const res = await request.get<never, IDirectoryDepartment[]>(
    `${endpoint.WORKSPACES}/${workspaceId}/directory/departments`,
    { params }
  );
  return res;
}

async function listAllDepartmentsWithPath(
  workspaceId: string
): Promise<IDirectoryDepartmentWithPath[]> {
  const departments = await listAllDepartments(workspaceId);

  // Create a map for quick lookup of departments by ID
  const departmentMap: { [key: string]: IDirectoryDepartment } = {};
  departments.forEach((dept) => {
    departmentMap[dept.id] = dept;
  });

  // Helper function to build the path for a department
  const buildPath = (department: IDirectoryDepartment): string => {
    if (!department.parent_id) {
      return department.name; // Root department
    }
    const parent = departmentMap[department.parent_id];
    return parent ? `${buildPath(parent)}/${department.name}` : department.name;
  };

  // Add the path to each department
  const departmentsWithPath: IDirectoryDepartmentWithPath[] = departments.map(
    (dept) => ({
      ...dept,
      path: buildPath(dept),
    })
  );

  // Sort the departments by path in ascending order
  departmentsWithPath.sort((a, b) => a.path.localeCompare(b.path));

  return departmentsWithPath;
}

async function createDepartment(
  workspaceId: string,
  data: {
    name: string;
    parent_id: string | null;
  }
): Promise<IDirectoryDepartment> {
  if (IS_FAKE) {
    console.log("calling createDepartment", workspaceId, data);
    const res: IDirectoryDepartment = {
      id: Date.now().toString(),
      name: data.name,
      parent_id: data.parent_id,
      created_time: Math.floor(Date.now() / 1000),
    };
    await delay(1000);
    return res;
  }

  const res = await request.post<never, IDirectoryDepartment>(
    `${endpoint.WORKSPACES}/${workspaceId}/directory/departments`,
    data
  );
  return res;
}

async function editDepartment(
  workspaceId: string,
  departmentId: string,
  data: {
    name?: string;
    parent_id?: string | null;
  }
): Promise<void> {
  if (IS_FAKE) {
    console.log("calling editDepartment", workspaceId, departmentId, data);
    await delay(1000);
    return;
  }

  await request.put<never, void>(
    `${endpoint.WORKSPACES}/${workspaceId}/directory/departments/${departmentId}`,
    data
  );
  return;
}

async function deleteDepartment(
  workspaceId: string,
  departmentId: string
): Promise<void> {
  if (IS_FAKE) {
    console.log("calling deleteDepartment", workspaceId, departmentId);
    await delay(1000);
    return;
  }

  await request.delete<never, void>(
    `${endpoint.WORKSPACES}/${workspaceId}/directory/departments/${departmentId}`
  );
  return;
}

const directoryService = {
  listUsers,
  syncUsersWithGoogle,
  multipleUpdateVPN,
  editUser,
  exportUsers,

  listAllDepartments,
  listAllDepartmentsWithPath,
  createDepartment,
  editDepartment,
  deleteDepartment,
};

export default directoryService;
